import React, {
  useCallback, useContext, useEffect, useReducer, useState,
} from 'react';
import {
  NavLink, Redirect, Route, useParams,
} from 'react-router-dom';
import { Card } from '@mui/material';
import { ModuleNav, Title } from 'shared/components/abstract/interactables';
import { SideNavContext } from 'shared/hoc/withSideNav';
import { getBuilding } from 'shared/api/buildings';
import { getUnit } from 'shared/api/units';
import {
  getTenant,
  getTenantBuilding,
  getTenantLocationHistory,
  getTenantUnit,
  tenantEditCheck,
} from 'shared/api/tenants';
import { checkPermissions } from 'shared/api/auth';
import { ArchivedBadge } from 'shared/components/ArchiveSwitch';
import { useRequest } from 'shared/hooks';
import { networkingViewCheck } from 'shared/api/networking';
import { searchTickets } from 'shared/api/support';
import { ActiveStyledBadge } from 'shared/components/styled/graphics';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';
import { macEditCheck } from 'features/tenant/api/macs';
import { tenantRadiusCheck } from 'shared/api/tenant-radius';
import { CardTab } from 'shared/components/styled/tabs';
import { getTenantPath, viewPath } from 'shared/routes/tenant';
import { formatTenantName, getTenantSubscriptionQos } from '../../shared/utils/formatters';

const initialState = {
  editEnabled: false,
  ...initialDataReducerState,
};

function tenantReducer(state, {
  action,
  payload,
}) {
  switch (action) {
    case 'set': {
      const {
        tenant,
        building,
      } = payload;
      if (!tenant) {
        return {
          ...initialState,
          data: null,
        };
      }

      return {
        ...state,
        previousBuilding: tenant.archivedAt ? building : undefined,
        building: tenant.archivedAt ? undefined : building,
        data: tenant,
        dirty: false,
        updatedData: {},
      };
    }
    case 'tenant': {
      if (payload === null) {
        return {
          ...initialState,
          data: null,
        };
      }

      return {
        ...state,
        data: { ...payload },
        updatedData: {},
        dirty: false,
      };
    }
    case 'reloadTenant':
      return {
        ...state,
        data: { ...state.data, ...payload },
        updatedData: {},
        dirty: false,
      };
    case 'onboard_account':
      return {
        ...state,
        data: {
          ...state.data,
          onboard_account: payload,
        },
      };
    case 'state':
      return { ...state, ...payload };
    default:
      return dataReducer(state, {
        action,
        payload,
      });
  }
}

/**
 * @param {Object} props
 * @param {Array<Object>} props.enabledRoutes - Array of enabled route objects
 * @param {Object} props.location - Location object from React Router
 */
export default function TenantNav({
  enabledRoutes,
  location,
}) {
  const { routeId } = useParams();
  const { request } = useRequest();
  const [updateCount, setUpdateCount] = useState(0);
  const [tenantState, tenantDispatch] = useReducer(tenantReducer, initialState);
  const syncSideNav = useContext(SideNavContext);
  const { data: tenant } = tenantState;

  useEffect(() => {
    (async () => {
      const response = await request(checkPermissions([
        tenantEditCheck,
        networkingViewCheck,
        macEditCheck,
        tenantRadiusCheck,
      ]));
      if (response) {
        tenantDispatch({
          action: 'state',
          payload: {
            editEnabled: response[0],
            canAccessNetworking: response[1],
            macEditCheck: response[2],
            radiusDataAccessPermission: response[3],
          },
        });
      }
    })();
  }, [request]);

  useEffect(() => {
    (async () => {
      const [
        tenantResponse,
        unitResponse,
        buildingResponse,
        ticketResponse,
      ] = await Promise.all([
        request(getTenant(routeId, '?expand=subscription')),
        request(getTenantUnit(routeId)),
        request(getTenantBuilding(routeId)),
        request(searchTickets(`?tenant_id=${routeId}&status=Active`)),
      ]);

      if (tenantResponse === undefined) return;
      let building = buildingResponse ? buildingResponse.data : null;
      const unit = unitResponse ? unitResponse.data : null;
      if (ticketResponse === undefined || ticketResponse === null) return;
      tenantResponse.tickets = ticketResponse.rows;

      if (tenantResponse) {
        const subQos = getTenantSubscriptionQos(tenantResponse);
        if (subQos.description) {
          tenantResponse.currentQos = subQos;
        } else if (building && building.qos) {
          tenantResponse.currentQos = building.qos;
        } else {
          tenantResponse.currentQos = {
            id: 0,
            description: 'None',
          };
        }
      }
      if (unit && building) {
        building.unit = unit;
      } else {
        // A tenant without a unit/building reference is considered archived.
        // Find the date that they were removed, and load their previous location.
        const locationHistory = await request(getTenantLocationHistory(routeId));
        if (!locationHistory) return;
        const hasHistory = locationHistory.length >= 2;
        if (hasHistory) {
          const { created_at: archivedAt } = locationHistory[0];
          const {
            building_id: previousBuildingId,
            unit_id: previousUnitId,
          } = locationHistory[1];
          tenantResponse.archivedAt = archivedAt;
          tenantResponse.previousUnitId = previousUnitId;

          const [previousBuilding, previousUnit] = await Promise.all([
            request(getBuilding(previousBuildingId)),
            request(getUnit(previousUnitId)),
          ]);
          if (previousBuilding && previousUnit) {
            building = previousBuilding;
            building.unit = previousUnit;
          }
        } else {
          tenantResponse.archivedAt = 'N/A';
        }
      }

      tenantDispatch({
        action: DataReducerActions.set,
        payload: {
          tenant: tenantResponse,
          building,
        },
      });
    })();
  }, [routeId, request, updateCount]);

  useEffect(() => {
    if (tenant) {
      syncSideNav(getTenantPath(tenant.id), formatTenantName(tenant));
    }
  }, [tenant, syncSideNav]);

  const reloadTenant = useCallback(async () => {
    const response = await request(getTenant(routeId, '?expand=subscription'));
    if (response) {
      tenantDispatch({
        action: 'reloadTenant',
        payload: response,
      });
    }
  }, [request, routeId]);

  const reloadBuilding = useCallback(() => setUpdateCount((c) => c + 1), []);

  if (tenant === undefined) return null;
  if (tenant === null) return <Redirect to={viewPath} />;

  const links = [];
  const routes = [];

  enabledRoutes.forEach(({
    RouteComponent,
    exact,
    getPath,
    renderWhen,
    text,
    filters = '',
    key = '',
  }) => {
    const path = getPath(routeId);

    if (!renderWhen || renderWhen(tenant, tenantState.building)) {
      links.push((
        text === 'Tickets' && tenant.tickets
          ? (
            <CardTab
              key={path}
              label={(
                <ActiveStyledBadge
                  key={path}
                  badgeContent={tenant.tickets.length}
                  overlap="circular"
                >
                  {text}
                </ActiveStyledBadge>
              )}
              value={path}
              component={NavLink}
              to={path}
              id={`simple-tabpanel-${text}`}
              aria-controls={`simple-tabpanel-${text}`}
            />
          ) : (
            <CardTab
              key={path}
              label={text}
              value={path}
              component={NavLink}
              to={path}
              id={`simple-tabpanel-${text}`}
              aria-controls={`simple-tabpanel-${text}`}
            />
          )
      ));
    }

    routes.push((
      <Route
        key={path}
        path={path}
        exact={exact}
        render={() => (
          <RouteComponent
            reloadTenant={reloadTenant}
            reloadBuilding={reloadBuilding}
            tenantDispatch={tenantDispatch}
            tenantState={tenantState}
            filterKey={key}
            savedFilters={filters}
            path={path}
          />
        )}
      />
    ));
  });

  return (
    <>
      <Title title={formatTenantName(tenant)}>
        <ArchivedBadge archivedAt={tenant.archivedAt} />
      </Title>
      <Card className={tenant.archivedAt ? 'archived' : ''}>
        <ModuleNav links={links} value={location.pathname} label="TenantNav" />
        {routes}
      </Card>
    </>
  );
}
